import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { ListOfPropertiesValues } from "./ListOfPropertiesValues";
import { Http } from "@status/codes";
import { FORM_MESSAGES } from "@/shared/constants/FormConstants";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { GENERAL_RULES_CONSTANTS } from "@/modules/GeneralRules/constants/GeneralRulesConstants";
import { FormTemplateService } from "@/modules/FormTemplate/services/FormTemplateService";
import {
  DATA_TYPE,
  SPECIAL_FIELDS,
  SPECIAL_FIELDS_DATA
} from "@/modules/DashboardTemplate/constants/DashboardTemplatesConstants";
import {
  FIELD_TYPE_STRATEGIES,
  DashboardFieldStrategy
} from "./FieldTypeStrategies";

function resolveDashboardStrategyType(
  strategy: DashboardFieldStrategy | undefined,
  field: any
): string {
  if (!strategy) {
    return DATA_TYPE.TEXT;
  }
  return typeof strategy.type === "function"
    ? strategy.type(field)
    : strategy.type;
}

function resolveDashboardStrategyOperators(
  strategy: DashboardFieldStrategy | undefined,
  field: any
): string {
  if (!strategy) {
    return "=";
  }
  return typeof strategy.operators === "function"
    ? strategy.operators(field)
    : strategy.operators;
}

export class FormsPropertiesValues extends ListOfPropertiesValues {
  formTemplate = this.getFormTemplates;
  owner = this.getDocument;
  createdBy = this.getUsers;
  changedBy = this.getUsers;
  firstAndLastForm = () => this.getFormSpecialFieldData(SPECIAL_FIELDS[3]);
  twoLastForms = () => this.getFormSpecialFieldData(SPECIAL_FIELDS[4]);
  secondToLastForm = () => this.getFormSpecialFieldData(SPECIAL_FIELDS[2]);
  lastForm = () => this.getFormSpecialFieldData(SPECIAL_FIELDS[1]);
  firstForm = () => this.getFormSpecialFieldData(SPECIAL_FIELDS[0]);
  lastDocId = "";
  keyword = "";
  indexSectionOfDashboard = 0;
  indexCollection = 0;

  async loadItems(
    field: string,
    idList?: string,
    keyWord?: string,
    lastDocId?: string
  ) {
    this.lastDocId = lastDocId || "";
    if (keyWord) {
      return (await this[field as keyof FormsPropertiesValues](
        keyWord
      )) as DynamicSelectData[];
    }

    if (this.keyword) {
      this.keyword = "";
    }

    if (idList) {
      return this.getList(idList);
    }

    return await this[field as keyof FormsPropertiesValues]();
  }

  setIndexes(
    indexSectionOfDashboard: number,
    indexCollectionFilter: number
  ): void {
    this.indexSectionOfDashboard = indexSectionOfDashboard;
    this.indexCollection = indexCollectionFilter;
  }

  async getFormTemplates(keyword?: string) {
    try {
      return await this.getFormTemplatesCommonFunction(this.lastDocId, keyword);
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async addFormFields(valueOfField: { id: string; name: string }[]) {
    try {
      const formTemplateService = new FormTemplateService();
      const response = await formTemplateService.getFormTemplateSchemaById(
        valueOfField[0].id
      );
      if (response.code === Http.Found && response.data) {
        const functionsToExecute = [
          {
            data: {
              id: "",
              name: "",
              fields: [] as { id: string; name: string }[],
              operators: "",
              type: "",
              listId: "",
              additionalLogic: "",
              isFormField: false,
              formTemplate: ""
            },
            function: "removeFormFields"
          }
        ];
        if (response.data!.fields.length > 0) {
          for (const field of response.data!.fields) {
            const matchedStrategy = FIELD_TYPE_STRATEGIES.find(s =>
              s.condition(field)
            );
            const type = resolveDashboardStrategyType(matchedStrategy, field);
            const operators = resolveDashboardStrategyOperators(
              matchedStrategy,
              field
            );
            functionsToExecute.push({
              data: {
                id: field.key,
                name: field.conversationTitle,
                fields: [] as { id: string; name: string }[],
                operators: operators,
                type: type,
                listId: field.list,
                additionalLogic: "",
                isFormField: true,
                formTemplate: valueOfField[0].id
              },
              function: "addNewField"
            });
          }
        }
        return functionsToExecute;
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getUsers(name?: string) {
    return await this.getUserByQuery(this.setFilters.bind(this), name);
  }

  setFilters(keyLastRef: string, name?: string) {
    const filterQuery: any = {
      state: GENERAL_RULES_CONSTANTS.ACTIVO
    };

    if (name && this.keyword !== name) {
      this.keyword = name;
      filterQuery.name = name;
      delete filterQuery[keyLastRef];
      return filterQuery;
    }

    if (name) {
      filterQuery.name = name;
      if (this.keyword !== name) {
        this.keyword = name;
      }
      return filterQuery;
    }

    return filterQuery;
  }

  getFormSpecialFieldData(fieldKey: string) {
    return SPECIAL_FIELDS_DATA[fieldKey] ?? { data: [] };
  }
}
